import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import Container from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import {
  AlertComp,
  ArrowRightIcon,
  Cancel,
  Clock,
  CountDown,
  TwoArrowsIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { globalColours, globalShadows } from '@classity-edtech/ui-base/dist/style/source';
import { Divider } from '@classity-edtech/ui-base/dist/components/Displays/Divider/Divider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyGetPaymentQuery } from '../../services/payment';
import { useEffect, useState } from 'react';
import { PaymentStatuses } from '@classity-edtech/platform-utils';
import { webPaths } from '../../routes/paths';
import Helmet from '../../components/Helmet';

export const BankTransferConfirmation = () => {
  const [searchParams] = useSearchParams();
  const reference = searchParams.get('reference');
  const transactionId = searchParams.get('transactionId');
  const bankName = searchParams.get('bankName');
  const bankAccount = searchParams.get('bankAccount');
  const amount = searchParams.get('amount');
  const expiredTimestamp = searchParams.get('expiredTimestamp');
  const navigate = useNavigate();
  const [loadingDelay, setLoadingDelay] = useState(true);

  const futureTime = parseInt(expiredTimestamp) * 1000;

  const isWithinFiveMinutes = dayjs().isAfter(dayjs(expiredTimestamp).subtract(5, 'minute'));

  const [payment, paymentResult] = useLazyGetPaymentQuery();

  const isPending = paymentResult?.data?.order?.paymentStatus === PaymentStatuses.PENDING;

  useEffect(() => {
    if (!reference) return;

    payment({ id: reference });

    const intervalId = setInterval(() => {
      payment({ id: reference });
    }, 10000);

    return () => clearInterval(intervalId);
  }, [reference]);

  useEffect(() => {
    if (paymentResult?.data?.order?.paymentStatus === PaymentStatuses.SUCCEEDED) {
      navigate(
        `/${webPaths?.orderConfirmation}?paymentId=${reference}&transactionId=${transactionId}&status=${'success'}`,
      );
    }
    if (paymentResult?.data?.order?.paymentStatus === PaymentStatuses.FAILED) {
      navigate(
        `/${webPaths?.orderConfirmation}?paymentId=${reference}&transactionId=${transactionId}&status=${'failed'}`,
      );
    }
  }, [paymentResult?.data?.order?.paymentStatus]);

  useEffect(() => {
    if (paymentResult.isLoading) {
      setLoadingDelay(true);
    } else {
      let loadingTimer = setTimeout(() => {
        setLoadingDelay(false);
      }, 3000);

      return () => clearTimeout(loadingTimer);
    }
  }, [paymentResult.isLoading]);

  return (
    <>
      <Helmet />
      <Box
        sx={{
          width: '100dvw',
          height: { xs: 'fit-content', md: '100dvh' },
          maxHeight: '100dvh',
          overflow: 'auto',
          display: { xs: 'block', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Cancel
          sx={{
            width: '16px',
            height: '16px',
            mt: 3,
            mr: 3,
            ml: 'auto',
            display: { xs: 'block', md: 'none' },
            cursor: 'pointer',
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 4,
            px: 3,
            py: 2,
            maxWidth: '800px',
            mx: 'auto',
            borderRadius: '16px',
            boxShadow: { xs: 'none', md: globalShadows.small },
            width: { xs: '100%', md: '800px' },
            height: { xs: 'max-content', md: '520px' },
          }}
        >
          {loadingDelay ? (
            <Container
              sx={{
                display: 'flex',
                borderRadius: '16px',
                flexDirection: 'column',
                mx: { xs: 0, md: 'auto' },
                px: 0,
                background: globalColours['$global-colour-background-elevated'],
                position: 'relative',
                height: '100%',
              }}
            >
              <Cancel
                sx={{
                  width: '16px',
                  height: '16px',
                  my: 2,
                  ml: 'auto',
                  display: { xs: 'none', md: 'block' },
                  cursor: 'pointer',
                }}
              />

              <Stack
                sx={{
                  pt: 3,
                  pb: 2,
                  height: '100%',
                }}
              >
                <Typography variant='subtitle4' sx={{ display: 'block' }}>
                  Checking Payment Status...
                </Typography>

                <Stack
                  sx={{
                    width: '100%',
                    height: { xs: '70dvh', md: '100%' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  <CircularProgress sx={{ width: '48px', height: '48px' }} />
                  <Typography variant='body3'>Please wait while we confirm payment...</Typography>
                </Stack>
              </Stack>
            </Container>
          ) : (
            <Container
              sx={{
                display: 'flex',
                borderRadius: '16px',
                flexDirection: 'column',
                mx: { xs: 0, md: 'auto' },
                px: 0,
                background: globalColours['$global-colour-background-elevated'],
                position: 'relative',
                overflowY: 'auto',
              }}
            >
              <Stack
                sx={{
                  pt: 3,
                  pb: 2,
                }}
              >
                <Typography
                  variant='subtitle4'
                  sx={{
                    display: 'block',
                    position: 'sticky',
                    top: 0,
                    background: globalColours['$global-colour-background-elevated'],
                    zIndex: 5,
                  }}
                >
                  Pay by Bank Transfer
                </Typography>

                {isPending && (
                  <AlertComp
                    sx={{ mb: 2 }}
                    titleElement={'Payment status'}
                    description={
                      'Your payment transfer has yet to be confirmed by our Payment Service Provider. Please wait for further updates or contact your Bank for details on your transfer status.'
                    }
                    sentiment='info'
                    handleClose={() => {}}
                    icon={
                      <TwoArrowsIcon
                        sx={theme => ({
                          color: theme?.palette?.common?.white,
                          width: '24px',
                          Height: '24px',
                        })}
                      />
                    }
                  />
                )}

                <Typography variant='body3'>
                  Please transfer the amount due from your bank account to the account listed below.
                  The process is safe and secure, and should arrive in seconds.
                </Typography>
              </Stack>
              <Box
                sx={{
                  borderRadius: '12px 12px 0 0',
                  background: globalColours['$global-colour-palette-gray-50'],
                  px: 3,
                  py: 2,
                  position: 'relative',
                  width: { xs: 'calc(100% - 16px)', md: '100%' },
                  mx: 'auto',
                }}
              >
                <Box
                  sx={{
                    pt: 1,
                  }}
                >
                  <Stack sx={{ gap: 2, maxWidth: '297px', mx: 'auto' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant='body3'>Bank Name</Typography>
                      <Typography variant='body3SemiBold'>{bankName}</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant='body3'>Account Number</Typography>
                      <Typography variant='body3SemiBold'>{bankAccount}</Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      my: 1,
                      maxWidth: '297px',
                      mx: 'auto',
                      borderTop: `dashed 1px ${globalColours['$global-colour-border-neutral']}`,
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 2,
                      maxWidth: '297px',
                      mx: 'auto',
                    }}
                  >
                    <Typography variant='body3'>Amount to Pay</Typography>
                    <Typography variant='body3SemiBold'>{amount}</Typography>
                  </Box>

                  <Divider sx={{ my: 3, maxWidth: '297px', mx: 'auto' }} />

                  <Stack sx={{ mt: 3, maxWidth: '358px', mx: 'auto', textAlign: 'center' }}>
                    <Typography variant='body3'>
                      This account is valid for this payment only and will expire in{' '}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Clock
                        sx={{
                          width: '16px',
                          height: '16px',
                          color: isWithinFiveMinutes
                            ? globalColours['$global-colour-sentiment-error-default']
                            : '',
                        }}
                      />
                      <Typography
                        variant='body3SemiBold'
                        sx={{
                          color: isWithinFiveMinutes
                            ? globalColours['$global-colour-sentiment-error-default']
                            : '',
                        }}
                      >
                        <CountDown start customized endTime={futureTime} endAction={() => {}} />
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    width: { xs: '16px', md: '40px' },
                    height: { xs: '16px', md: '18px' },
                    background: globalColours['$global-colour-palette-gray-50'],
                    borderRadius: '50%',
                    position: 'absolute',
                    left: { xs: '-8px', md: '-20px' },
                    top: '50%',
                  }}
                ></Box>
                <Box
                  sx={{
                    width: { xs: '16px', md: '40px' },
                    height: { xs: '16px', md: '18px' },
                    background: globalColours['$global-colour-palette-gray-50'],
                    borderRadius: '50%',
                    position: 'absolute',
                    right: { xs: '-8px', md: '-20px' },
                    top: '50%',
                  }}
                ></Box>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  top: '-5px',
                  px: { xs: 1, md: 2.5 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, md: 2 },
                }}
              >
                {Array(15)
                  .fill('')
                  .map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: '36px',
                        height: '10px',
                        background: globalColours['$global-colour-background-elevated'],
                        borderRadius: '50%',
                      }}
                    ></Box>
                  ))}
              </Box>

              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  p: 3,
                  gap: 4,
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'sticky',
                  bottom: 0,
                  background: globalColours['$global-colour-background-elevated'],
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                  <Cancel sx={{ width: '16px', height: '16px' }} />
                  <Typography variant='body3'>Close</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: globalColours['$global-colour-interactive-control-default'],
                    cursor: 'pointer',
                  }}
                  onClick={() => payment({ id: reference })}
                >
                  <Typography variant='body3'>
                    {paymentResult?.data?.order?.paymentStatus === PaymentStatuses.PENDING
                      ? 'Check Payment Status'
                      : 'I have completed the Bank Transfer'}
                  </Typography>
                  <ArrowRightIcon sx={{ width: '24px', height: '24px' }} />
                </Box>
              </Box>
            </Container>
          )}
        </Box>

        {!loadingDelay && (
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              color: globalColours['$global-colour-interactive-control-default'],
              py: 1,
              px: 3,
              position: 'fixed',
              bottom: 0,
              width: '100%',
              minHeight: '54px',
              boxShadow: globalShadows.small,
            }}
          >
            <Typography variant='body3'>
              {!isPending ? 'Check Payment Status' : 'I have completed the Bank Transfer'}
            </Typography>
            <ArrowRightIcon sx={{ width: '24px', height: '24px' }} />
          </Box>
        )}
      </Box>
    </>
  );
};

BankTransferConfirmation.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

BankTransferConfirmation.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(BankTransferConfirmation)(
  ({ theme }) => `
`,
);
